import {
  EXTERNAL_MARKUP_TYPES,
  GENERIC_DATASET_TYPE_HINTS,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";
import { SINGLE_SCAN_TYPE_HINTS } from "@faro-lotv/project-source";
import {
  LightweightElement,
  LightweightElementsRecord,
} from "./lightweight-elements-slice";

/**
 * @returns true if the given element is a Section with a specific type hint
 * @param element element to check
 * @param typeHint The type hint that the section has to have.
 */
export function isLightweightSectionWithTypeHint<Hint extends IElementTypeHint>(
  element: LightweightElement,
  typeHint: Hint,
): element is LightweightElement & {
  type: IElementType.section;
  typeHint: Hint;
} {
  return element.type === IElementType.section && element.typeHint === typeHint;
}

/**
 * @returns True if the lightweight element is a dataset in the capture tree
 * @param element The lightweight element to check
 */
export function isLightweightDataSetCapture(
  element: LightweightElement,
): element is LightweightElement & {
  type: IElementType.section;
  typeHint: IElementTypeHint.dataSetCapture;
} {
  return isLightweightSectionWithTypeHint(
    element,
    IElementTypeHint.dataSetCapture,
  );
}

/**
 * @returns True if the lightweight element is an area
 * @param element The lightweight element to check
 */
export function isLightweightArea(
  element: LightweightElement,
): element is LightweightElement & {
  type: IElementType.section;
  typeHint: IElementTypeHint.area;
} {
  return isLightweightSectionWithTypeHint(element, IElementTypeHint.area);
}

/**
 * @returns True if the lightweight element is a data session
 * @param element The lightweight element to check
 */
export function isLightweightDataSession(
  element: LightweightElement,
): element is LightweightElement & {
  type: IElementType.section;
  typeHint: IElementTypeHint.dataSession;
} {
  return isLightweightSectionWithTypeHint(
    element,
    IElementTypeHint.dataSession,
  );
}

/** Helper type to widen the type of GENERIC_DATASET_TYPE_HINTS and allow usage of .includes() */
const GENERIC_DATASET_TYPE_HINTS_WIDE_TYPE: ReadonlyArray<
  string | null | undefined
> = GENERIC_DATASET_TYPE_HINTS;

/**
 * @returns True if the lightweight element is a generic dataset
 * @param element The lightweight element to check
 */
export function isLightweightGenericDataset(
  element: LightweightElement,
): element is LightweightElement & {
  type: IElementType.section;
  typeHint: (typeof GENERIC_DATASET_TYPE_HINTS)[number];
} {
  return (
    element.type === IElementType.section &&
    GENERIC_DATASET_TYPE_HINTS_WIDE_TYPE.includes(element.typeHint)
  );
}

/**
 * @returns True if the lightweight element is the section of a single scan, i.e. a scan
 * that lives inside the capture tree (as opposed to e.g. a room section outside of it).
 * @param element The lightweight element to check
 */
export function isLightweightSingleScanSection(
  element: LightweightElement,
): element is LightweightElement & {
  type: IElementType.section;
  typeHint: (typeof SINGLE_SCAN_TYPE_HINTS)[number];
} {
  return isLightweightWithTypeAndHints(
    element,
    IElementType.section,
    SINGLE_SCAN_TYPE_HINTS,
  );
}

/**
 * @returns true if the given iElement is a Section-Room.
 * @param iElement element to check
 */
export function isLightweightRoomSection(
  iElement: LightweightElement,
): iElement is LightweightElement & {
  type: IElementType.section;
  typeHint: IElementTypeHint.room;
} {
  return isLightweightSectionWithTypeHint(iElement, IElementTypeHint.room);
}

/**
 * @returns true if the given lightweight element is an IElementImg360.
 * @param element element to check
 */
export function isLightweightImg360(
  element: LightweightElement,
): element is LightweightElement & { type: IElementType.img360 } {
  return element.type === IElementType.img360;
}

/**
 * @returns true if the given lightweight element is an IElementImg2d.
 * @param element element to check
 */
export function isLightweightImg2d(
  element: LightweightElement,
): element is LightweightElement & { type: IElementType.img2d } {
  return element.type === IElementType.img2d;
}

/**
 * @returns true if the given lightweight element is an IElementMarkup.
 * @param element element to check
 */
export function isLightweightMarkup(
  element: LightweightElement,
): element is LightweightElement & { type: IElementType.markup } {
  return element.type === IElementType.markup;
}

/**
 * @returns true if the given lightweight element is an external markup (Autodesk, Procore).
 * @param element element to check
 */
export function isLightweightExternalMarkup(
  element: LightweightElement,
): boolean {
  return EXTERNAL_MARKUP_TYPES.has(element.type);
}

/**
 * @returns true if the given lightweight element is a Sphere XG markup or an external markup (Autodesk, Procore).
 * @param element element to check
 */
export function isLightweightMarkupOrExternalMarkup(
  element: LightweightElement,
): boolean {
  return isLightweightMarkup(element) || isLightweightExternalMarkup(element);
}

/**
 * @param element to check
 * @param type to check
 * @param hint to check
 * @returns true if the element has the proper type and type-hint
 */
export function isLightweightWithTypeAndHint<
  Type extends IElementType,
  Hint extends IElementTypeHint,
>(
  element: LightweightElement,
  type: Type,
  hint: Hint,
): element is LightweightElement & { type: Type; typeHint: Hint } {
  return element.type === type && element.typeHint === hint;
}

/**
 * @param element to check
 * @param type to check
 * @param hints List of possible type hints for this lightweight element
 * @returns true if the element has the proper type and type-hint (one listed in `hints`)
 */
export function isLightweightWithTypeAndHints<
  Element extends LightweightElement,
  Type extends IElementType,
  Hints extends IElementTypeHint,
>(
  element: Element,
  type: Type,
  hints: readonly Hints[],
): element is Element & { type: Type; typeHint: Hints } {
  return element.type === type && hints.some((h) => h === element.typeHint);
}

/**
 * @returns true if the given iElement is a time series of room.
 * @param element element to check
 */
export function isLightweightTimeSeriesRoom(
  element: LightweightElement,
): element is LightweightElement & {
  type: IElementType.timeSeries;
  typeHint: IElementTypeHint.room;
} {
  return (
    element.type === IElementType.timeSeries &&
    element.typeHint === IElementTypeHint.room
  );
}

/**
 * @returns true if the given iElement is a time series data session.
 * @param element element to check
 */
export function isLightweightTimeSeriesDataSession(
  element: LightweightElement,
): element is LightweightElement & {
  type: IElementType.timeSeries;
  typeHint: IElementTypeHint.dataSession;
} {
  return (
    element.type === IElementType.timeSeries &&
    element.typeHint === IElementTypeHint.dataSession
  );
}

/**
 * @returns true if the given lightweight element is a group of attachments
 * @param element element to check
 */
export function isLightweightAttachmentsGroup(
  element: LightweightElement,
): element is LightweightElement & {
  type: IElementType.group;
  typeHint: IElementTypeHint.attachments;
} {
  return (
    element.type === IElementType.group &&
    element.typeHint === IElementTypeHint.attachments
  );
}

/**
 * @returns true if the given lightweight element is a group of nodes
 * @param element element to check
 */
export function isLightweightNodesGroup(
  element: LightweightElement,
): element is LightweightElement & {
  type: IElementType.group;
  typeHint: IElementTypeHint.nodes;
} {
  return (
    element.type === IElementType.group &&
    element.typeHint === IElementTypeHint.nodes
  );
}

/**
 * @returns true if the given lightweight element is a image sheet (either simple or tiled)
 * @param element element to check
 */
export function isLightweightGenericSheet(
  element: LightweightElement,
): element is LightweightElement & {
  type: IElementType.imgSheet | IElementType.imgSheetTiled;
} {
  return (
    element.type === IElementType.imgSheet ||
    element.type === IElementType.imgSheetTiled
  );
}

/**
 * @returns True if the input element is a location marker
 * @param element The element to check
 * @param elements The lightweight elements record of the app
 */
export function isLightweightLocationMarker(
  element: LightweightElement,
  elements: LightweightElementsRecord,
): element is LightweightElement {
  if (
    element.type !== IElementType.section ||
    element.typeHint !== IElementTypeHint.room
  ) {
    return false;
  }

  if (!element.parentId) {
    return false;
  }

  const parent = elements[element.parentId];
  if (
    parent?.type !== IElementType.group ||
    parent.typeHint !== IElementTypeHint.rooms
  ) {
    return false;
  }

  const stack = [element];
  while (stack.length) {
    const child = stack.pop();
    if (!child) return false;

    // If the timeseries room has children, this is not a location marker
    if (
      child.type === IElementType.timeSeries &&
      child.typeHint === IElementTypeHint.room &&
      child.childrenIds?.length
    ) {
      return false;
    }

    // If the attachments group has children, this is not a location marker
    if (
      child.type === IElementType.group &&
      child.typeHint === IElementTypeHint.attachments &&
      child.childrenIds?.length
    ) {
      return false;
    }

    for (const id of child.childrenIds ?? []) {
      const next = elements[id];
      if (next) stack.push(next);
    }
  }
  return true;
}
